import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Mail, Phone, MapPin, Send, Facebook, Instagram, Youtube } from 'lucide-react';
import { toast } from 'sonner';

export const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    eventType: '',
    date: '',
    location: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock form submission
    console.log('Form submitted:', formData);
    toast.success('Booking request sent! We\'ll get back to you soon.', {
      duration: 4000,
      position: 'top-center',
    });
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      eventType: '',
      date: '',
      location: '',
      message: '',
    });
  };

  return (
    <section
  id="contact"
  ref={ref}
  className="relative py-24 lg:py-32 bg-black overflow-hidden"
>
  {/* Background Effects */}
  <div className="absolute inset-0 opacity-20">
    <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
    <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-green-500 rounded-full blur-3xl" />
  </div>

  <div className="container mx-auto px-6 lg:px-16 relative z-10">
    {/* Section Header */}
    <motion.div
      initial={{ opacity: 0, y: -30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: -30 }}
      transition={{ duration: 0.8 }}
      className="text-center mb-16"
    >
      <h2 className="text-5xl md:text-7xl font-display font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-purple-400 mb-6 uppercase">
        Book MasterPlan
      </h2>
      <div className="w-32 h-1 bg-gradient-to-r from-green-400 to-purple-400 mx-auto mb-6" />
      <p className="text-xl text-gray-400 max-w-3xl mx-auto">
        Let's create an unforgettable worship experience together
      </p>
    </motion.div>

    {/* --- CHANGED: Removed grid, added flex justify-center --- */}
    <div className="flex justify-center">
      {/* Contact Info Wrapper */}
      <motion.div
        initial={{ opacity: 0, y: 50 }} 
        animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.8 }}
        // --- CHANGED: Added w-full and max-w-2xl to control width in the center ---
        className="space-y-8 w-full max-w-2xl"
      >
        {/* Contact Details */}
        <div className="bg-gradient-to-br from-gray-900/50 to-purple-900/30 backdrop-blur-xl border border-purple-500/20 rounded-3xl p-8 space-y-6">
          <h3 className="text-2xl font-bold text-green-400 mb-6 uppercase text-center">
            Get In Touch
          </h3>

          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-600/30 rounded-xl">
              <MapPin className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Location</h4>
              <p className="text-gray-400">Dubai, United Arab Emirates</p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-600/30 rounded-xl">
              <Mail className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Email</h4>
              <a
                href="mailto:info@masterplan.band"
                className="text-gray-400 hover:text-green-400 transition-colors duration-300"
              >
                info@masterplan.band
              </a>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="p-3 bg-purple-600/30 rounded-xl">
              <Phone className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <h4 className="text-white font-semibold mb-1">Phone</h4>
              <a
                href="tel:+971501234567"
                className="text-gray-400 hover:text-green-400 transition-colors duration-300"
              >
                +971508212869
              </a>
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div
          id="socialmedia"
          className="bg-gradient-to-br from-gray-900/50 to-purple-900/30 backdrop-blur-xl border border-purple-500/20 rounded-3xl p-8"
        >
          <h3 className="text-2xl font-bold text-green-400 mb-6 uppercase text-center">
            Follow Us
          </h3>
          <div className="flex gap-4 justify-center">
            <a
              href="https://www.facebook.com/share/19wzZ7C95W/?mibextid=wwXIfr"
              className="p-4 bg-purple-600/30 rounded-xl hover:bg-purple-600/50 transition-all duration-300 hover:scale-110 group"
              aria-label="Facebook"
            >
              <Facebook className="w-6 h-6 text-gray-400 group-hover:text-green-400 transition-colors duration-300" />
            </a>
            <a
              href="https://www.instagram.com/masterplanuae?igsh=MXF0OXp6bzk0cHhvYQ=="
              className="p-4 bg-purple-600/30 rounded-xl hover:bg-purple-600/50 transition-all duration-300 hover:scale-110 group"
              aria-label="Instagram"
            >
              <Instagram className="w-6 h-6 text-gray-400 group-hover:text-green-400 transition-colors duration-300" />
            </a>
            <a
              href="https://youtube.com/@masterplanuae?si=oiI9Bqsc5jA4e5eP"
              className="p-4 bg-purple-600/30 rounded-xl hover:bg-purple-600/50 transition-all duration-300 hover:scale-110 group"
              aria-label="YouTube"
            >
              <Youtube className="w-6 h-6 text-gray-400 group-hover:text-green-400 transition-colors duration-300" />
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  </div>
</section>
  );
};
